import type { AccessRequestPublic, ChecklistItemPublic, UserPublic } from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../i18n';
import { ApiError } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import {
  listLifecycleChecklists,
  reopenChecklistItem,
  tickChecklistItem,
} from '../../../lib/lifecycleApi';
import { queryKeys } from '../../../lib/queryKeys';
import { showToast } from '../../../lib/toast';
import { Callout, Card, CardHeader, Chip, EmptyRow } from '../../../ui/Primitives';
import { eyebrowStyle, monoStyle, rowActionStyle } from '../../../ui/styles';

import { hasItWork } from '../access/itEmail/checklist';
import { ChecklistItEmailDialog } from '../access/itEmail/ChecklistItEmailDialog';
import { useChecklistItEmail } from '../access/itEmail/useChecklistItEmail';

const onboardingChecklistKey = (userId: string) =>
  ['admin', 'access', 'lifecycle', 'checklists', 'onboarding', userId] as const;

export function OnboardingChecklist({
  user,
  requests,
}: {
  user: UserPublic;
  requests: readonly AccessRequestPublic[];
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const itEmail = useChecklistItEmail();
  const checklistQuery = useQuery({
    queryKey: onboardingChecklistKey(user.id),
    queryFn: () => listLifecycleChecklists({ userId: user.id, kind: 'onboarding' }),
  });
  const checklist = checklistQuery.data?.data.find((entry) => entry.retiredAt === null) ?? null;
  const pending = requests.filter(
    (request) => request.source === 'onboarding' && request.status === 'pending',
  );

  function refreshed() {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: onboardingChecklistKey(user.id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
  }

  function failed(failure: unknown) {
    setError(failure instanceof ApiError ? failure.message : STR.errors.generic);
  }

  const tick = useMutation({
    mutationFn: (item: ChecklistItemPublic) => tickChecklistItem(item.id),
    onSuccess: (result) => {
      refreshed();
      showToast(
        STR.access.onboarding.toastDone(
          result.item.record?.catalogItem.name ?? STR.access.onboarding.checklistTitle,
        ),
      );
    },
    onError: failed,
  });
  const reopen = useMutation({
    mutationFn: (item: ChecklistItemPublic) => reopenChecklistItem(item.id),
    onSuccess: (result) => {
      refreshed();
      showToast(
        STR.access.onboarding.toastReopened(
          result.item.record?.catalogItem.name ?? STR.access.onboarding.checklistTitle,
        ),
        'warn',
      );
    },
    onError: failed,
  });

  if (checklistQuery.isLoading) return null;
  if (checklistQuery.isError) {
    return (
      <Card>
        <CardHeader title={STR.access.onboarding.checklistTitle} />
        <EmptyRow>{STR.access.onboarding.checklistLoadFailed}</EmptyRow>
      </Card>
    );
  }
  if (checklist === null) return null;

  const items = checklist.items.filter(
    (item) => item.retiredAt === null && item.record !== null && item.action === 'set_up',
  );
  const done = items.filter((item) => item.status === 'done').length;
  const total = items.length + pending.length;
  const percent = total === 0 ? 100 : Math.round((done / total) * 100);
  const busyId = tick.variables?.id ?? reopen.variables?.id ?? null;

  return (
    <Card style={{ borderLeft: '3px solid var(--bg-info-soft)' }}>
      <CardHeader
        title={STR.access.onboarding.checklistTitle}
        aside={
          <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
            {checklist.completedAt === null
              ? STR.access.onboarding.checklistProgress(done, total, formatDate(checklist.createdAt))
              : STR.access.onboarding.checklistComplete}
          </span>
        }
        action={
          hasItWork(checklist) ? (
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              disabled={itEmail.draft.isPending}
              onClick={() => {
                itEmail.draft.mutate(checklist, { onError: failed });
              }}
            >
              {itEmail.draft.isPending ? STR.itEmails.drafting : STR.itEmails.draftItEmail}
            </button>
          ) : undefined
        }
      />
      <div style={{ height: 4, background: 'var(--track)' }}>
        <div
          aria-hidden="true"
          style={{ height: 4, width: `${String(percent)}%`, background: 'var(--bg-success)' }}
        />
      </div>
      {error === null ? null : (
        <div style={{ padding: '12px 18px 0' }}>
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        </div>
      )}
      {items.length === 0 ? null : (
        <>
          <div
            className="tx-subtle"
            style={{ ...eyebrowStyle, padding: '9px 18px', borderBottom: '1px solid var(--border)' }}
          >
            {STR.access.onboarding.checklistIt}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {items.map((item) => {
              const record = item.record;
              if (record === null) return null;
              const itemBusy = busyId === item.id;
              return (
                <li
                  key={item.id}
                  className="row"
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: '11px 18px',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={item.status === 'done'}
                    disabled={tick.isPending || reopen.isPending}
                    aria-label={
                      item.status === 'done'
                        ? STR.access.onboarding.reopen
                        : STR.access.onboarding.markDone
                    }
                    onChange={() => {
                      if (item.status === 'done') reopen.mutate(item);
                      else tick.mutate(item);
                    }}
                    style={{ marginTop: 3 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {record.catalogItem.name}
                      <span className="tx-subtle" style={{ fontWeight: 500 }}>
                        {' · '}
                        {record.path.map((entry) => entry.name).join(' · ') || STR.accessCommon.notPlaced}
                      </span>
                    </div>
                    <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
                      <span style={monoStyle}>{record.login ?? STR.access.onboarding.noLogin}</span>
                      {item.status === 'done' && item.doneAt !== null
                        ? ` · ${STR.access.onboarding.done(formatDate(item.doneAt))}`
                        : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="tbtn"
                    style={rowActionStyle}
                    disabled={tick.isPending || reopen.isPending}
                    onClick={() => {
                      if (item.status === 'done') reopen.mutate(item);
                      else tick.mutate(item);
                    }}
                  >
                    {itemBusy
                      ? STR.common.loading
                      : item.status === 'done'
                        ? STR.access.onboarding.reopen
                        : STR.access.onboarding.markDone}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {pending.length === 0 ? null : (
        <>
          <div
            className="tx-subtle"
            style={{ ...eyebrowStyle, padding: '9px 18px', borderBottom: '1px solid var(--border)' }}
          >
            {STR.access.onboarding.checklistWaiting}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {pending.map((request) => (
              <li
                key={request.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  padding: '11px 18px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span className="tx-subtle" aria-hidden="true">
                  ◷
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{request.catalogItem.name}</div>
                  <div className="tx-subtle" style={{ fontSize: 11.5 }}>
                    {STR.access.onboarding.checklistWaitingLine(formatDate(request.createdAt))}
                  </div>
                </div>
                <Chip tone="planned" size="sm">
                  {STR.accessCommon.requestStatus.pending}
                </Chip>
              </li>
            ))}
          </ul>
        </>
      )}
      {items.length === 0 && pending.length === 0 ? (
        <EmptyRow>{STR.access.onboarding.checklistEmpty}</EmptyRow>
      ) : null}
      {itEmail.open === null ? null : (
        <ChecklistItEmailDialog draft={itEmail.open} onClose={itEmail.close} />
      )}
    </Card>
  );
}
